import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CopyPlus,
  FileArchive,
  ShieldCheck,
} from 'lucide-react';
import apiClient from '@/api/client';
import {
  Card,
  DiffTable,
  WarningLine,
  downloadBlob,
  downloadJson,
  fileStamp,
  showNotes,
} from './backupShared';

// ─── Sicherung eines einzelnen Stammbaums ────────────────────────────────────
//
// Arbeitet auf dem Baum, der als Prop hereinkommt – nicht auf dem gerade
// aktiven. In der Verwaltung wird der Baum ausgewählt, und die Auswahl darf sich
// nicht mit dem unterscheiden, was man beim Arbeiten offen hat.

interface Preview {
  tree: { id: string; name: string };
  current: Record<string, number>;
  incoming: Record<string, number>;
  exportedAt: string;
  includesMedical: boolean;
  warnings: string[];
  files: {
    /** Dateien, die die Sicherung mitbringt. */
    incoming: number;
    /** Dateien, die aktuell im Archiv liegen. */
    current: number;
    /** In der Datenbank vermerkt, aber auf der Platte nicht gefunden. */
    currentMissing: number;
  };
}

export function TreeBackupPanel({
  treeId,
  treeName,
}: {
  treeId: string;
  treeName: string;
}) {
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirmName, setConfirmName] = useState('');
  const [busy, setBusy] = useState<
    null | 'export' | 'exportZip' | 'preview' | 'restore' | 'import'
  >(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Der Baum kommt als Prop, nicht aus dem Store: der Header muss explizit mit.
  const headers = { 'X-Tree-Id': treeId };

  const reset = useCallback(() => {
    setFile(null);
    setPreview(null);
    setConfirmName('');
    if (fileRef.current) fileRef.current.value = '';
  }, []);

  const handleExport = async (withFiles: boolean) => {
    setBusy(withFiles ? 'exportZip' : 'export');
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      const base = treeName || 'stammbaum';

      if (withFiles) {
        const res = await apiClient.get('/tree-backup/export', {
          params: { files: 1 },
          responseType: 'blob',
          headers,
        });
        downloadBlob(res.data as Blob, `${base}-${stamp}.zip`);
        const missing = Number(res.headers['x-files-missing'] ?? 0);
        const included = Number(res.headers['x-files-included'] ?? 0);
        toast.success(
          missing > 0
            ? `Archiv heruntergeladen (${included} Dateien, ${missing} fehlen auf der Platte).`
            : `Archiv heruntergeladen (${included} Dateien).`,
        );
        return;
      }

      const { data } = await apiClient.get('/tree-backup/export', { headers });
      downloadJson(data, `${base}-${stamp}.json`);
      toast.success('Sicherung heruntergeladen.');
    } catch {
      toast.error('Export fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleFile = async (f: File) => {
    setFile(f);
    setPreview(null);
    setConfirmName('');
    setBusy('preview');
    try {
      const form = new FormData();
      form.append('file', f);
      const { data } = await apiClient.post<Preview>('/tree-backup/preview', form, { headers });
      setPreview(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Datei konnte nicht gelesen werden.');
      reset();
    } finally {
      setBusy(null);
    }
  };

  const handleRestore = async () => {
    if (!file) return;
    setBusy('restore');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('confirmName', confirmName);
      const { data } = await apiClient.post('/tree-backup/restore', form, { headers });

      // Sicherungspunkt sofort anbieten: damit bleibt auch eine versehentliche
      // Wiederherstellung umkehrbar.
      downloadJson(data.safetySnapshot, `sicherungspunkt-vor-wiederherstellung-${fileStamp()}.json`);

      await queryClient.invalidateQueries();
      const f = data.files;
      toast.success(
        f
          ? `Wiederhergestellt, ${f.written} Dateien geschrieben. Sicherungspunkt heruntergeladen.`
          : 'Stammbaum wiederhergestellt. Der Sicherungspunkt wurde heruntergeladen.',
      );
      showNotes(data.notes);
      reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Wiederherstellung fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const handleImportAsNew = async () => {
    if (!file) return;
    setBusy('import');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await apiClient.post('/tree-backup/import-as-new', form, { headers });
      toast.success(`Als neuer Stammbaum „${data.tree.name}" angelegt.`);
      showNotes(data.notes);
      await queryClient.invalidateQueries();
      reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  const nameMatches = confirmName.trim() === (preview?.tree.name ?? treeName);

  return (
    <div className="space-y-4">
      <Card
        title="Herunterladen"
        description="Speichert alle Daten dieses Stammbaums – Personen, Beziehungen, Quellen, Belege, Laufbahn, Lebensläufe und medizinische Angaben."
        icon={ArrowDownToLine}
      >
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => void handleExport(false)}
            disabled={busy !== null}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === 'export' ? 'Wird erstellt…' : 'Nur Daten (JSON)'}
          </button>
          <button
            onClick={() => void handleExport(true)}
            disabled={busy !== null}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <FileArchive className="h-4 w-4" aria-hidden="true" />
            {busy === 'exportZip' ? 'Wird gepackt…' : 'Mit Archiv-Dateien (ZIP)'}
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
          Die JSON-Datei enthält nur die Daten – für eine Wiederherstellung auf
          <em> dieser</em> Installation genügt sie, weil die Dateien im Archiv liegen bleiben.
          Für einen Umzug auf einen anderen Server das ZIP nehmen: es enthält die Dateien mit.
        </p>
      </Card>

      <Card
        title="Aus einer Stammbaum-Sicherung wiederherstellen"
        description="Sicherungsdatei auswählen – JSON oder ZIP. Ersetzt den Inhalt dieses Stammbaums. Vor dem Überschreiben wird automatisch ein Sicherungspunkt des jetzigen Standes heruntergeladen."
        icon={ArrowUpFromLine}
        tone="danger"
      >
        <input
          ref={fileRef}
          type="file"
          // Beide Exportformate: die reine JSON-Datei und das ZIP mit den
          // Archiv-Dateien. Der Server erkennt das Format am Inhalt.
          accept="application/json,.json,application/zip,.zip"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void handleFile(f);
          }}
          className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200"
        />

        {busy === 'preview' && <p className="mt-3 text-xs text-slate-500">Datei wird geprüft…</p>}

        {preview && (
          <div className="mt-4 space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
              <p className="mb-2 text-xs text-slate-500">
                Erstellt am {new Date(preview.exportedAt).toLocaleString('de-DE')}
                {preview.includesMedical ? ' · mit medizinischen Daten' : ' · ohne medizinische Daten'}
              </p>
              <DiffTable current={preview.current} incoming={preview.incoming} />

              {/* Archiv-Dateien getrennt ausweisen: sie liegen nicht in der
                  Datenbank und verhalten sich bei der Wiederherstellung anders. */}
              <p className="mt-2 flex items-start gap-1.5 border-t border-slate-200 pt-2 text-[11px] leading-relaxed text-slate-500">
                <FileArchive className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span>
                  {preview.files.incoming > 0
                    ? `Die Sicherung bringt ${preview.files.incoming} Archiv-Dateien mit. Vorhandene gleicher Größe werden übersprungen, fehlende geschrieben; nichts wird gelöscht.`
                    : 'Die Sicherung enthält keine Archiv-Dateien. Die Dateien im Archiv bleiben unverändert.'}
                  {preview.files.currentMissing > 0 &&
                    ` Achtung: ${preview.files.currentMissing} in der Datenbank vermerkte Dateien fehlen aktuell auf der Platte.`}
                </span>
              </p>
            </div>

            {preview.warnings.map((w) => (
              <WarningLine key={w}>{w}</WarningLine>
            ))}

            <div className="rounded-lg border border-red-200 bg-red-50/60 p-3">
              <label htmlFor="tree-confirm" className="block text-xs font-medium text-red-800">
                Zur Bestätigung den Namen des Stammbaums eingeben:{' '}
                <span className="font-mono">{preview.tree.name}</span>
              </label>
              <input
                id="tree-confirm"
                type="text"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={preview.tree.name}
                autoComplete="off"
                className="mt-1.5 w-full rounded border border-red-300 bg-white px-2.5 py-2 text-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={handleRestore}
                  disabled={!nameMatches || busy !== null}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {busy === 'restore' ? 'Wird wiederhergestellt…' : 'Überschreiben'}
                </button>
                <button
                  onClick={reset}
                  disabled={busy !== null}
                  className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                >
                  Abbrechen
                </button>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs leading-relaxed text-emerald-800">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              Der jetzige Stand wird vor dem Überschreiben als Datei heruntergeladen. Damit lässt
              sich die Wiederherstellung selbst wieder rückgängig machen.
            </div>
          </div>
        )}
      </Card>

      {preview && (
        <Card
          title="Stattdessen als neuen Stammbaum anlegen"
          description="Legt einen zusätzlichen Stammbaum aus der Datei an, ohne etwas zu überschreiben. Sinnvoll beim Umzug zwischen Installationen oder um einem Verwandten einen Zweig zu geben."
          icon={CopyPlus}
        >
          <button
            onClick={handleImportAsNew}
            disabled={busy !== null}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {busy === 'import' ? 'Wird angelegt…' : 'Als neuen Stammbaum anlegen'}
          </button>
        </Card>
      )}
    </div>
  );
}
